import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, PageHeader } from "@/components/dashboard/shared";
import { AudiencePicker, AudienceOption, TEST_AUDIENCE } from "@/components/dashboard/audience-picker";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";
import { cn } from "@/lib/utils";

type Summary = {
  sent_this_month: number;
  sent: number;
  delivered: number;
  delivery_rate: number;
  opened: number;
  open_rate: number;
  failed: number;
  failed_rate: number;
  webhook_events: boolean;
};
type Audience = {
  key: string;
  label: string;
  category: AudienceOption["category"];
  needs_city: boolean;
  recipients: number | null;
};
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
type Campaign = {
  id: string;
  subject: string;
  audience: string;
  audience_label: string;
  status: string;
  recipient_count: number;
  sent_count: number;
  failed_count: number;
  delivered: number;
  opened: number;
  created_at: string;
  sent_at: string | null;
};

const STATUS_STYLE: Record<string, string> = {
  sending: "bg-warning-soft text-warning-foreground",
  sent: "bg-success-soft text-success",
  failed: "bg-danger-soft text-destructive",
};

function apiGet<T>(path: string): Promise<T> {
  return fetch(`${getAPIBaseURL()}${path}`, { headers: getAdminHeaders() }).then(async (response) => {
    if (!response.ok) throw new Error(`Request failed (${response.status})`);
    return response.json() as Promise<T>;
  });
}

function formatDate(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function StatCard({ title, value, note, tone }: { title: string; value: string; note: string; tone?: string }) {
  return (
    <Card className="rounded-2xl">
      <CardContent className="space-y-2 p-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
        <p className="text-3xl font-semibold tracking-tight">{value}</p>
        <p className={cn("text-xs", tone ?? "text-muted-foreground")}>{note}</p>
      </CardContent>
    </Card>
  );
}

export function MailingPage() {
  const queryClient = useQueryClient();
  const summary = useQuery({
    queryKey: ["mailing-summary"],
    queryFn: () => apiGet<Summary>("/api/v1/admin/mailing/summary"),
  });
  const [city, setCity] = React.useState("");
  const [testEmail, setTestEmail] = React.useState("");
  const audiences = useQuery({
    queryKey: ["mailing-audiences", city],
    queryFn: () =>
      apiGet<{ audiences: Audience[] }>(
        `/api/v1/admin/mailing/audiences${city ? `?city=${encodeURIComponent(city)}` : ""}`,
      ),
  });
  const cities = useQuery({
    queryKey: ["audience-cities"],
    queryFn: () => apiGet<{ cities: string[] }>("/api/v1/admin/audiences/cities"),
  });
  const campaigns = useQuery({
    queryKey: ["mailing-campaigns"],
    queryFn: () => apiGet<{ campaigns: Campaign[] }>("/api/v1/admin/mailing/campaigns"),
    refetchInterval: (query) => (query.state.data?.campaigns.some((c) => c.status === "sending") ? 5000 : false),
  });

  const [subject, setSubject] = React.useState("");
  const [body, setBody] = React.useState("");
  const [audience, setAudience] = React.useState("");
  const [confirmed, setConfirmed] = React.useState(false);

  const selected = audiences.data?.audiences.find((a) => a.key === audience);
  const isTest = audience === TEST_AUDIENCE;
  const cityMissing = !isTest && !!selected?.needs_city && !city;
  const recipientCount = isTest ? 1 : selected?.recipients ?? 0;
  const canSend =
    subject.trim() !== "" &&
    body.trim() !== "" &&
    confirmed &&
    (isTest ? EMAIL_PATTERN.test(testEmail.trim()) : !!selected && !cityMissing && recipientCount > 0);
  const pickerOptions: AudienceOption[] = (audiences.data?.audiences ?? []).map((a) => ({
    key: a.key,
    label: a.label,
    category: a.category,
    needs_city: a.needs_city,
    count: a.needs_city && !city ? null : a.recipients,
  }));

  const send = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/mailing/campaigns`, {
        method: "POST",
        headers: { ...getAdminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: subject.trim(),
          body: body.trim(),
          audience,
          city: selected?.needs_city ? city : undefined,
          test_email: isTest ? testEmail.trim() : undefined,
          confirmed_recipients: recipientCount,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error ?? "Could not start the campaign");
      return payload;
    },
    onSuccess: (payload) => {
      toast.success(`Sending to ${payload.recipient_count} people…`);
      setSubject("");
      setBody("");
      setConfirmed(false);
      queryClient.invalidateQueries({ queryKey: ["mailing-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["mailing-summary"] });
      queryClient.invalidateQueries({ queryKey: ["mailing-audiences"] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
      queryClient.invalidateQueries({ queryKey: ["mailing-audiences"] });
    },
  });

  const s = summary.data;
  const pending = !s?.webhook_events;

  return (
    <div className="space-y-6">
      <PageHeader title="Mailing" description="Create targeted email campaigns and measure delivery." />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Emails sent" value={(s?.sent ?? 0).toLocaleString("en-US")} note={`${(s?.sent_this_month ?? 0).toLocaleString("en-US")} this month`} />
        <StatCard
          title="Delivered"
          value={pending ? "—" : (s?.delivered ?? 0).toLocaleString("en-US")}
          note={pending ? "Waiting for delivery reports" : `${s?.delivery_rate ?? 0}% delivery`}
          tone="text-success"
        />
        <StatCard
          title="Opened"
          value={pending ? "—" : (s?.opened ?? 0).toLocaleString("en-US")}
          note={pending ? "Waiting for open reports" : `${s?.open_rate ?? 0}% open rate`}
        />
        <StatCard title="Failed" value={(s?.failed ?? 0).toLocaleString("en-US")} note={`${s?.failed_rate ?? 0}% of sends`} tone="text-destructive" />
      </div>

      <Card className="rounded-2xl">
        <CardContent className="space-y-5 p-6">
          <div>
            <h3 className="text-base font-semibold">New campaign</h3>
            <p className="text-sm text-muted-foreground">Nothing is sent until you confirm the number of recipients.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Subject</Label>
              <Input value={subject} maxLength={150} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Audience</Label>
              <AudiencePicker
                audiences={pickerOptions}
                value={audience}
                onChange={(key) => {
                  setAudience(key);
                  setConfirmed(false);
                }}
                city={city}
                onCityChange={(name) => {
                  setCity(name);
                  setConfirmed(false);
                }}
                cities={cities.data?.cities ?? []}
                testSelected={isTest}
                onTestSelect={() => {
                  setAudience(TEST_AUDIENCE);
                  setConfirmed(false);
                }}
              />
              {isTest && (
                <Input
                  type="email"
                  placeholder="name@example.com"
                  value={testEmail}
                  onChange={(e) => {
                    setTestEmail(e.target.value);
                    setConfirmed(false);
                  }}
                />
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Message</Label>
            <textarea
              value={body}
              maxLength={10000}
              onChange={(e) => setBody(e.target.value)}
              rows={7}
              className="w-full rounded-md border bg-background p-3 text-sm"
              placeholder="Write the message. Line breaks are kept."
            />
          </div>

          {isTest && EMAIL_PATTERN.test(testEmail.trim()) && (
            <div className="rounded-xl bg-muted/40 p-4 text-sm">
              This test email will be sent to <span className="font-semibold">{testEmail.trim()}</span> only.
            </div>
          )}
          {!isTest && selected && !cityMissing && (
            <div className="rounded-xl bg-muted/40 p-4 text-sm">
              This campaign will be sent to <span className="font-semibold">{recipientCount.toLocaleString("en-US")}</span> people in “{selected.label}”{city && selected.needs_city ? ` (${city})` : ""}.
            </div>
          )}

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} disabled={!canSend && !confirmed} />
            I confirm this email should be sent to these people.
          </label>

          <Button className="rounded-xl" disabled={!canSend || send.isPending} onClick={() => send.mutate()}>
            {send.isPending ? "Starting…" : "Send campaign"}
          </Button>
        </CardContent>
      </Card>

      <Card className="overflow-hidden rounded-2xl">
        <CardContent className="p-0">
          <div className="border-b px-5 py-4">
            <h2 className="text-base font-semibold">Campaigns</h2>
          </div>
          {campaigns.isLoading ? (
            <div className="space-y-3 p-4">
              {[1, 2, 3].map((n) => (
                <Skeleton key={n} className="h-11 w-full" />
              ))}
            </div>
          ) : campaigns.isError ? (
            <div className="p-6 text-sm text-destructive">Campaigns could not be loaded.</div>
          ) : !campaigns.data?.campaigns.length ? (
            <EmptyState title="No campaigns yet" message="Campaigns you send will appear here." />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Audience</TableHead>
                    <TableHead className="text-right">Recipients</TableHead>
                    <TableHead className="text-right">Sent</TableHead>
                    <TableHead className="text-right">Failed</TableHead>
                    <TableHead className="text-right">Delivered</TableHead>
                    <TableHead className="text-right">Opened</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.data.campaigns.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">{c.subject}</TableCell>
                      <TableCell className="text-sm">{c.audience_label}</TableCell>
                      <TableCell className="text-right">{c.recipient_count.toLocaleString("en-US")}</TableCell>
                      <TableCell className="text-right">{c.sent_count.toLocaleString("en-US")}</TableCell>
                      <TableCell className="text-right text-destructive">{c.failed_count.toLocaleString("en-US")}</TableCell>
                      <TableCell className="text-right">{pending ? "—" : c.delivered.toLocaleString("en-US")}</TableCell>
                      <TableCell className="text-right">{pending ? "—" : c.opened.toLocaleString("en-US")}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">{formatDate(c.sent_at ?? c.created_at)}</TableCell>
                      <TableCell>
                        <span className={cn("inline-flex rounded-full px-2.5 py-1 text-xs font-medium capitalize", STATUS_STYLE[c.status] ?? "bg-muted")}>
                          {c.status}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
